import moment from 'moment'

import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isStandaloneExperience, isOnlyExperiencesOrder } from 'lib/experiences/orderExperienceClassification'
import { getCustomOfferTravelWindow } from './customOfferDates'
import { isCruiseItemCancelledOrFailed, isCustomOfferItemVisible } from './orderUtils'

function afterToday(date: string) {
  return moment.utc(date, ISO_DATE_FORMAT).isSameOrAfter(moment.utc(), 'day')
}

function inThePast(date: string) {
  return moment.utc(date, ISO_DATE_FORMAT).isBefore(moment.utc(), 'day')
}

function isBetween(start: string | moment.Moment, end: string | moment.Moment) {
  return moment().isBetween(moment(start), moment(end), 'day')
}

export function isItemCurrentOrCloseToCheckIn(item: App.OrderItem) {
  if (item.status === 'cancelled') {
    return false
  }

  if (!item.reservation) {
    return false
  }

  return isItemCurrent(item) || (isItemUpcoming(item) && moment.utc().add(2, 'days').isSameOrAfter(item.reservation.startDate))
}

export function isItemBedbankCurrentOrCloseToCheckIn(item: App.OrderBedbankItem) {
  if (item.status === 'cancelled') {
    return false
  }

  return isItemBedbankCurrent(item) || (isItemBedbankUpcoming(item) && moment.utc().add(2, 'days').isSameOrAfter(item.checkIn))
}

export function isItemUpcoming(item: App.OrderItem) {
  if (item.status === 'cancelled') {
    return false
  }

  if (!item.reservationMade || !item.reservation) {
    return !(item.offer.bookByDate && inThePast(item.offer.bookByDate))
  }

  return afterToday(item.reservation.startDate)
}

export function isItemBedbankUpcoming(item: App.OrderBedbankItem) {
  // downgradeType does not always imply an item is inactive
  // some items are a date change with the downgradeType tag
  // we must also check for isDowngraded tag
  // should be done at mapper level https://aussiecommerce.atlassian.net/browse/CRO-4508
  if (item.status === 'cancelled' || (item.downgradeType && item.isDowngraded)) {
    return false
  }

  return afterToday(item.checkIn.format(ISO_DATE_FORMAT))
}

/** Completed stay, cancelled booking, or otherwise not upcoming / in-stay — for Book again gating. */
export function isHotelOrderItemPastOrCancelled(item: App.OrderItem): boolean {
  if (item.status === 'cancelled') {
    return true
  }

  return !isItemUpcoming(item) && !isItemCurrent(item)
}

/** Completed stay, cancelled booking, or otherwise not upcoming / in-stay — for Book again gating. */
export function isBedbankOrderItemPastOrCancelled(item: App.OrderBedbankItem): boolean {
  if (item.status === 'cancelled') {
    return true
  }

  return !isItemBedbankUpcoming(item) && !isItemBedbankCurrent(item)
}

export function isItemTourUpcoming(item: App.Tours.TourV2OrderItem) {
  if (item.status === 'cancelled') {
    return false
  }

  return afterToday(moment(item.tour.startDate).format(ISO_DATE_FORMAT))
}

function isInsuranceUpcoming(item: App.OrderInsuranceItem) {
  if (item.status === 'cancelled') return false
  return afterToday(item.endDate)
}

export function isFlightUpcoming(item: App.OrderFlightItem) {
  return item.departureDate ? afterToday(item.departureDate) : false
}

function isItemCurrent(item: App.OrderItem) {
  if (item.status === 'cancelled') {
    return false
  }

  if (item.reservation) {
    return isBetween(item.reservation.startDate, item.reservation.endDate)
  }
  return false
}

function isItemBedbankCurrent(item: App.OrderBedbankItem) {
  if (item.status === 'cancelled') {
    return false
  }

  return isBetween(item.checkIn, item.checkOut)
}

function isInsuranceCurrent(item: App.OrderInsuranceItem) {
  if (item.status === 'cancelled') {
    return false
  }

  return isBetween(item.startDate, item.endDate)
}

function isFlightCurrent(item: App.OrderFlightItem) {
  if (item.status === 'cancelled') {
    return false
  }

  return item.departureDate && item.arrivalDate ? isBetween(item.departureDate, item.arrivalDate) : false
}

// this always returns true for open date standalone experiences but correct way to classify this is by markedAsRedeemed, but that is not available in the order object
export function isExperienceItemUpcoming(item: App.OrderExperienceItem, order: App.Order) {
  if (item.status === 'cancelled') return false

  // this can be an open date experiences as well, but we consider it as not upcoming if the expiry date in the past with status as completed
  if (isExperienceItemExpired(item)) {
    return false
  }

  // always consider standalone open date experiences as upcoming. If non-standalone return false so isUpcoming function will check the other items
  // the correct way to classify this is by markedAsRedeemed, but that is not available in the order object
  if (!item.date) return isStandaloneExperience(item, order)

  return afterToday(item.date)
}

export function isCustomOfferActive(item: App.OrderCustomOfferItem, order: App.Order, isSpoofed = false) {
  if (item.status === 'cancelled' || !isCustomOfferItemVisible(order, item, isSpoofed)) {
    return false
  }

  const travelWindow = getCustomOfferTravelWindow(item)

  // An offer with no dates at all is an open-dated quote -- keep it active.
  if (!travelWindow) {
    return true
  }

  return afterToday(travelWindow.end)
}

export function isTransferItemUpcoming(item: App.OrderTransferItem) {
  return item.status !== 'cancelled' && afterToday(item.date)
}

export function isExperienceItemExpired(item: App.OrderExperienceItem) {
  return item.status === 'completed' && item.expirationDate && inThePast(item.expirationDate)
}

export function isExperienceItemCurrent(item: App.OrderExperienceItem) {
  return item.status !== 'cancelled' && item.date && moment(item.date).isSame(moment(), 'day') && !isExperienceItemExpired(item)
}

export function isTransferItemCurrent(item: App.OrderTransferItem) {
  return item.status !== 'cancelled' && item.date && moment(item.date).isSame(moment(), 'day')
}

export function isCustomOfferItemCurrent(item: App.OrderCustomOfferItem, order: App.Order, isSpoofed = false) {
  if (item.status === 'cancelled' || !isCustomOfferItemVisible(order, item, isSpoofed)) {
    return false
  }

  const travelWindow = getCustomOfferTravelWindow(item)

  // An offer with no dates at all is an open-dated quote -- keep it active.
  if (!travelWindow) {
    return true
  }

  return isBetween(travelWindow.start, travelWindow.end)
}

export function isItemTourCurrent(item: App.Tours.TourV2OrderItem) {
  if (item.status === 'cancelled') {
    return false
  }

  return isBetween(moment(item.tour.startDate).format(ISO_DATE_FORMAT), moment(item.tour.endDate).format(ISO_DATE_FORMAT))
}

export function isCruiseItemUpcoming(item: App.OrderCruiseItem) {
  if (isCruiseItemCancelledOrFailed(item)) return false
  return afterToday(moment(item.departureDate).format(ISO_DATE_FORMAT))
}

export function isCarHireItemUpcoming(item: App.OrderCarHireItem) {
  if (item.status === 'cancelled' || !item.reservation?.pickUp?.date) return false
  return afterToday(moment(item.reservation?.pickUp?.date).format(ISO_DATE_FORMAT))
}

export function isCruiseItemCurrent(item: App.OrderCruiseItem) {
  if (isCruiseItemCancelledOrFailed(item)) return false
  return isBetween(moment(item.departureDate).format(ISO_DATE_FORMAT), moment(item.arrivalDate).format(ISO_DATE_FORMAT))
}

export function isCarHireItemCurrent(item: App.OrderCarHireItem) {
  if (item.status === 'cancelled' || !item.reservation?.pickUp?.date) return false
  return isBetween(moment(item.reservation?.pickUp?.date).format(ISO_DATE_FORMAT), moment(item.reservation?.dropOff?.date).format(ISO_DATE_FORMAT))
}

export function isMerchandiseItemUpcoming(item: App.OrderMerchandiseItem) {
  if (item.status === 'cancelled') return false
  // TODO: not implemented yet, I believe we won't be even looking at 'upcoming' or not in future changes
  return true
}

export function isMerchandiseItemCurrent(item: App.OrderMerchandiseItem) {
  if (item.status === 'cancelled') return false
  // TODO: not implemented yet, I believe we won't be even looking at 'upcoming' or not in future changes
  return true
}

export function isMerchandiseItemPast(item: App.OrderMerchandiseItem) {
  if (item.status === 'cancelled') return false
  // TODO: not implemented yet, I believe we won't be even looking at 'upcoming' or not in future changes
  return false
}

export function isOrderCurrent(order: App.Order, isSpoofed = false) {
  return order.items.some(isItemCurrent) ||
    order.bedbankItems.some(isItemBedbankCurrent) ||
    order.insuranceItems.some(isInsuranceCurrent) ||
    order.flightItems.some(isFlightCurrent) ||
    order.tourItems.some(isItemTourCurrent) ||
    order.experienceItems.some(isExperienceItemCurrent) ||
    order.cruiseItems.some(isCruiseItemCurrent) ||
    order.carHireItems.some(isCarHireItemCurrent) ||
    order.transferItems.some(isTransferItemCurrent) ||
    order.customOfferItems.some(item => isCustomOfferItemCurrent(item, order, isSpoofed)) ||
    order.merchandiseItems.some(isMerchandiseItemCurrent)
}

// this always returns true for open date standalone experiences but correct way to classify this is by markedAsRedeemed, but that is not available in the order object
export default function isUpcoming(order: App.Order, isSpoofed = false) {
  return order.items.some(isItemUpcoming) ||
    order.bedbankItems.some(isItemBedbankUpcoming) ||
    order.insuranceItems.some(isInsuranceUpcoming) ||
    order.flightItems.some(isFlightUpcoming) ||
    order.tourItems.some(isItemTourUpcoming) ||
    order.experienceItems.some(item => isExperienceItemUpcoming(item, order)) ||
    order.cruiseItems.some(isCruiseItemUpcoming) ||
    order.carHireItems.some(isCarHireItemUpcoming) ||
    order.transferItems.some(isTransferItemUpcoming) ||
    order.customOfferItems.some(item => isCustomOfferActive(item, order, isSpoofed)) ||
    order.merchandiseItems.some(isMerchandiseItemUpcoming)
}

export function isCurrentOrCloseToCheckIn(order: App.Order) {
  return order.items.some(isItemCurrentOrCloseToCheckIn) ||
    order.bedbankItems.some(isItemBedbankCurrentOrCloseToCheckIn)
}

export function isOrderPast(order: App.Order) {
  return !isUpcoming(order) && !isOrderCurrent(order)
}

export function isOnlyExperiencesOrderUpcoming(order: App.Order) {
  if (isOnlyExperiencesOrder(order)) {
    return order.experienceItems.some(item => {
      if (item.status === 'cancelled') return false

      if (isExperienceItemExpired(item)) {
        return false
      }

      if (!item.date) return true

      return afterToday(item.date)
    })
  }

  return false
}
